/**
 * WeightDiscrepancyPage — `/mail/weight-discrepancy`.
 *
 * Live operator surface backed by service-mail
 *   POST /v1/mail-items/:id/weight-adjustments
 *   GET  /v1/weight-adjustments
 *
 * Two stacked panels:
 *   - "Submit new adjustment": tracking number lookup → declared weight is
 *     pre-filled from the item.package.weightKg, operator enters scanned
 *     weight and fee delta. Backend computes status + creates a supervisor
 *     request when fee delta ≥ ₦10k.
 *   - "Pending queue": list of recent adjustments with status pills.
 */
import { type FormEvent, type JSX, useEffect, useMemo, useState } from 'react';
import { Loader2, Scale } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { getMailItem, listMailItems } from '../../lib/api/mail-items';
import {
  listWeightAdjustments,
  submitWeightAdjustment,
  type WeightAdjustmentDto,
  type WeightAdjustmentStatus,
} from '../../lib/api/weightAdjustments';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

interface ItemSummary {
  readonly id: string;
  readonly trackingNumber: string;
  readonly status: string;
  readonly serviceClass: string;
  readonly declaredWeightGrams: number;
}

function readDeclaredWeightGrams(pkg: unknown): number {
  if (!pkg || typeof pkg !== 'object') return 0;
  const p = pkg as { weightGrams?: number; weightKg?: number | string };
  if (typeof p.weightGrams === 'number') return Math.round(p.weightGrams);
  const wk = p.weightKg;
  if (typeof wk === 'number') return Math.round(wk * 1000);
  if (typeof wk === 'string') {
    const f = parseFloat(wk);
    if (!Number.isNaN(f)) return Math.round(f * 1000);
  }
  return 0;
}

function statusPillClass(status: WeightAdjustmentStatus): string {
  switch (status) {
    case 'AUTO_APPLIED': return 'bg-green-50 text-green-700';
    case 'PENDING_APPROVAL': return 'bg-yellow-50 text-yellow-700';
    case 'APPROVED': return 'bg-blue-50 text-blue-700';
    case 'REJECTED': return 'bg-red-50 text-red-700';
  }
}

export function WeightDiscrepancyPage(): JSX.Element {
  const [tn, setTn] = useState('');
  const [item, setItem] = useState<ItemSummary | null>(null);
  const [scannedKg, setScannedKg] = useState('');
  const [feeDeltaNgn, setFeeDeltaNgn] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<readonly WeightAdjustmentDto[]>([]);
  const [statusFilter, setStatusFilter] = useState<WeightAdjustmentStatus | ''>('PENDING_APPROVAL');
  const [listError, setListError] = useState<string | null>(null);

  async function refreshList(): Promise<void> {
    try {
      const res = await listWeightAdjustments({
        ...(statusFilter && { status: statusFilter }),
        pageSize: 50,
      });
      setItems(res.items);
      setListError(null);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load adjustments');
      setItems([]);
    }
  }

  useEffect(() => {
    void refreshList();
    // note: deps intentionally narrow — rule not enabled in this config
  }, [statusFilter]);

  async function handleScan(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const tracking = tn.trim().toUpperCase();
    if (!tracking) return;
    setBusy(true);
    setLookupError(null);
    setItem(null);
    setSubmitError(null);
    try {
      const res = await listMailItems({ trackingNumber: tracking, pageSize: 1 });
      const found = res.items[0];
      if (!found) {
        setLookupError(`No mail item found for ${tracking}.`);
        return;
      }
      // The list endpoint returns summaries (no package JSON); fetch the
      // detail to read the declared weight.
      const detail = await getMailItem(found.id);
      const declared = readDeclaredWeightGrams(detail.package);
      setItem({
        id: detail.id,
        trackingNumber: detail.trackingNumber,
        status: detail.status,
        serviceClass: detail.serviceClass,
        declaredWeightGrams: declared,
      });
    } catch (e) {
      setLookupError(
        e instanceof ApiError ? `Lookup failed (${e.status})` : (e instanceof Error ? e.message : 'Lookup failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  const declaredGrams = item?.declaredWeightGrams ?? 0;
  const scannedGrams = (() => {
    const f = parseFloat(scannedKg);
    return Number.isNaN(f) ? 0 : Math.round(f * 1000);
  })();
  const deltaGrams = scannedGrams && declaredGrams ? scannedGrams - declaredGrams : 0;
  const deltaPct = declaredGrams > 0 ? (Math.abs(deltaGrams) / declaredGrams) * 100 : 0;
  const feeDeltaMinor = (() => {
    const f = parseFloat(feeDeltaNgn);
    return Number.isNaN(f) ? 0 : Math.round(f * 100);
  })();
  const willRouteToSupervisor = Math.abs(feeDeltaMinor) >= 1_000_000;
  const meetsThreshold = Math.abs(deltaGrams) >= 100 || deltaPct >= 5;

  const summary = useMemo(() => {
    if (!item || !scannedGrams) return null;
    const sign = deltaGrams >= 0 ? '+' : '';
    return `${sign}${deltaGrams}g (${deltaPct.toFixed(1)}%)`;
  }, [item, scannedGrams, deltaGrams, deltaPct]);

  async function onSubmit(): Promise<void> {
    if (!item) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitWeightAdjustment(item.id, {
        scannedWeightGrams: scannedGrams,
        feeDeltaMinor,
      });
      // Reset form, refresh queue
      setScannedKg('');
      setFeeDeltaNgn('');
      setItem(null);
      setTn('');
      await refreshList();
    } catch (e) {
      setSubmitError(
        e instanceof ApiError ? `${e.status}: ${(e.body as { message?: string })?.message ?? e.message}` : (e instanceof Error ? e.message : 'Submit failed'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-5" data-testid="weight-discrepancy-page">
      <PageHeader
        title="Weight Discrepancy Adjustments"
        description="Re-weigh items at sorting; large fee deltas route to a supervisor."
      />

      <div className="bg-white border border-border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <Scale className="w-4 h-4 text-nipost-green" /> Submit a new adjustment
        </h3>
        <form onSubmit={(e) => void handleScan(e)} className="flex gap-3 mb-3">
          <input
            type="text"
            value={tn}
            onChange={(e) => setTn(e.target.value)}
            placeholder="Tracking number (EE…)"
            data-testid="wd-tracking-input"
            className="flex-1 px-3 py-2.5 text-base font-mono uppercase border border-border rounded-lg"
          />
          <button
            type="submit"
            disabled={busy || tn.trim().length === 0}
            data-testid="wd-lookup"
            className="px-6 py-2.5 bg-nipost-green text-white font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lookup'}
          </button>
        </form>
        {lookupError ? <p className="text-sm text-red-700">{lookupError}</p> : null}
        {item ? (
          <div className="border-t border-border pt-3 space-y-3" data-testid="wd-item-panel">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-base font-bold text-gray-900">{item.trackingNumber}</p>
                <p className="text-xs text-muted">Service {item.serviceClass} · declared {item.declaredWeightGrams}g</p>
              </div>
              <StatusBadge status={item.status} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="wd-scanned" className="text-xs font-semibold text-muted uppercase">Scanned (kg)</label>
                <input
                  id="wd-scanned"
                  type="number"
                  step="0.001"
                  min="0"
                  value={scannedKg}
                  onChange={(e) => setScannedKg(e.target.value)}
                  data-testid="wd-scanned-input"
                  className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg"
                />
              </div>
              <div>
                <label htmlFor="wd-fee" className="text-xs font-semibold text-muted uppercase">Fee delta (₦, signed)</label>
                <input
                  id="wd-fee"
                  type="number"
                  step="0.01"
                  value={feeDeltaNgn}
                  onChange={(e) => setFeeDeltaNgn(e.target.value)}
                  data-testid="wd-fee-input"
                  className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg"
                />
              </div>
            </div>
            {summary ? (
              <p className="text-sm">
                <span className="font-semibold">Delta:</span> {summary}
                {!meetsThreshold ? (
                  <span className="text-xs text-muted ml-2">(below threshold — submit will be refused)</span>
                ) : willRouteToSupervisor ? (
                  <span className="text-xs text-yellow-700 ml-2">(will route to supervisor)</span>
                ) : (
                  <span className="text-xs text-green-700 ml-2">(auto-applied)</span>
                )}
              </p>
            ) : null}
            {submitError ? <p className="text-sm text-red-700">{submitError}</p> : null}
            <button
              type="button"
              onClick={() => void onSubmit()}
              disabled={submitting || !scannedGrams || !meetsThreshold}
              data-testid="wd-submit"
              className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit adjustment'}
            </button>
          </div>
        ) : null}
      </div>

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Recent adjustments</h3>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as WeightAdjustmentStatus | '')}
            data-testid="wd-status-filter"
            className="px-2 py-1.5 text-sm border border-border rounded-lg"
          >
            <option value="">All</option>
            <option value="AUTO_APPLIED">Auto-applied</option>
            <option value="PENDING_APPROVAL">Pending approval</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
        {listError ? <p className="text-sm text-red-700">{listError}</p> : null}
        {items.length === 0 ? (
          <p className="text-sm text-muted" data-testid="wd-list-empty">No matching adjustments.</p>
        ) : (
          <ul className="space-y-2" data-testid="wd-adjustments-list">
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`wd-row-${it.id}`}
                className="border border-border rounded-lg p-3 flex items-start justify-between gap-3"
              >
                <div>
                  <p className="font-mono text-xs font-bold text-gray-900">{it.trackingNumber}</p>
                  <p className="text-xs text-muted mt-0.5">
                    {it.declaredWeightGrams}g → {it.scannedWeightGrams}g (Δ {it.deltaGrams >= 0 ? '+' : ''}{it.deltaGrams}g)
                    {' · '}fee {it.feeDeltaMinor >= 0 ? '+' : ''}₦{(it.feeDeltaMinor / 100).toLocaleString()}
                  </p>
                  <p className="text-[11px] text-muted mt-0.5">{new Date(it.createdAt).toLocaleString()}</p>
                </div>
                <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded ${statusPillClass(it.status)}`}>
                  {it.status.replace(/_/g, ' ')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
